import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import type Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import type Individual from "marketplace/individuals/individual";
import { personPermissionsPath } from "permissions/_views/people/person-permissions-path";
import { type NameState, personStateText } from "permissions/_views/targets/permission-target-state";
import { accessibleNames } from "permissions/_views/wording/permission-wording";
import type { ReactNode } from "react";
import { Link as RouterLink } from "react-router";

/**
 * The person a grant row leads with on a page about a kind or a record: an optional checkbox, the
 * avatar, the name linking to the person's page, and the email beneath it.
 *
 * A person the directory did not name is still listed, and says why (`docs/design/permissions/
 * PERMISSIONS_UI_DESIGN.md`, "Target and person states"): skeleton text while the lookup is under way,
 * otherwise the person state in place of the name — "No longer a member", "A person you can't see",
 * or "Couldn't load this person" with Retry. The link still opens their page, where their grants are.
 */
export default function HolderCell(
  props: Readonly<{
    userId: Guid;
    individual?: Individual;
    state: NameState;
    /** Asks the directory again, for a person whose lookup failed. */
    onRetry?: () => void;
    select?: ReactNode;
    children?: ReactNode;
  }>,
) {
  const { state } = props;
  const named = state.state === "found";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      {props.select}
      {/* On a phone the checkbox and the switch leave the name little room; the avatar gives way. */}
      <Box sx={{ display: { xs: "none", sm: "block" }, flexShrink: 0 }}>
        <IndividualAvatar individual={props.individual} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        {state.state === "loading" ? (
          <Skeleton variant="text" width="8rem" aria-hidden />
        ) : (
          <Link
            component={RouterLink}
            to={personPermissionsPath(props.userId)}
            underline="hover"
            variant="body2"
            color={named ? undefined : "text.secondary"}
            sx={{ fontWeight: named ? 500 : undefined, overflowWrap: "anywhere" }}
          >
            {named ? state.name : personStateText(state)}
          </Link>
        )}
        {state.state === "failed" && props.onRetry && (
          <Button size="small" onClick={props.onRetry} aria-label={accessibleNames.retryPerson} sx={{ ml: 1 }}>
            Retry
          </Button>
        )}
        {props.individual?.email && (
          // One line: an address broken mid-word reads as two. The whole address is its title.
          <Typography variant="caption" color="text.secondary" component="p" noWrap title={props.individual.email.value}>
            {props.individual.email.value}
          </Typography>
        )}
        {props.children}
      </Box>
    </Box>
  );
}
